import React from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  Loader,
  Modal,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { ExportReportFormat } from "../../../../../../redux/slices/reports/Types";
import {
  getPartnersReportsSagaAction,
  getReportFormatsListSagaAction,
  saveReportFormatSagaAction,
  deleteReportFormatSagaAction,
} from "../../../../../../redux/saga/reports/saga-actions";
import "./ExportReportModal.scss";
import DeleteModal from "../../../../../../common/components/DeleteModal";

interface ExportReportModalProps {
  partnerFilterProp: {
    partnerName: string[];
    status: string[];
    type: string[];
    tier: string[];
    vertical: string[];
    revenueStream: string[];
    accountManager: string[];
    region: string[];
    country: string[];
    contractName: string[];
    contractStatus: string[];
    supportLevel: string[];
    startDateRange: string[];
    endDateRange: string[];
  };
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type FieldSection = "partnerFields" | "contractFields" | "revenueFields";

const ExportReportModal: React.FC<ExportReportModalProps> = ({
  partnerFilterProp,
  setModalOpen,
}) => {
  const [exportFields, setExportFields] = React.useState<ExportReportFormat>({
    partnerFields: {
      partner_name: false,
      country: false,
      account_manager: false,
      region: false,
      address: false,
      partner_type: false,
      partner_tier: false,
      partner_vertical: false,
      website: false,
      description: false,
      revenue_stream: false,
      phone_number: false,
      partner_potential: false,
      support_level: false,
      registration_date: false,
      partner_status: false,
    },
    contractFields: {
      contract_status: false,
      new_contract: false,
      work_package_type: false,
      subscription_enabled: false,
      start_date: false,
      end_date: false,
    },
    revenueFields: {
      work_package_amount: false,
    },
  });
  const [selectedFormat, setSelectedFormat] = React.useState(null);
  const [saveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  const { reportFormatsList, status } = useAppSelector(
    (state) => state.reports
  );
  const dispatch = useAppDispatch();

  const reportFormatOptions = React.useMemo(
    () =>
      reportFormatsList.map((reportFormat) => ({
        label: reportFormat.saved_export_format,
        value: reportFormat.ID,
      })),
    [reportFormatsList]
  );

  React.useEffect(() => {
    dispatch(getReportFormatsListSagaAction());
  }, [dispatch]);

  React.useEffect(() => {
    setSelectedFormat(null);
  }, [reportFormatOptions]);

  const onCheckboxClick = (
    eventResult: {
      checked: boolean;
      value: string;
    },
    tableName: FieldSection
  ) => {
    setExportFields((prevState) => ({
      ...prevState,
      [tableName]: {
        ...prevState[tableName],
        [eventResult.value]: eventResult.checked,
      },
    }));
  };

  const onSelectAllClick = (tableName: FieldSection, checked: boolean) => {
    setExportFields((prevState) => ({
      ...prevState,
      [tableName]: Object.keys(prevState[tableName]).reduce(
        (acc, key) => ({
          ...acc,
          [key]: checked,
        }),
        {}
      ),
    }));
  };

  const isAllSelected = (tableName: FieldSection): boolean => {
    const fields = exportFields[tableName];
    return Object.values(fields).every((value) => value === true);
  };

  const isIndeterminate = (tableName: FieldSection): boolean => {
    const fields = exportFields[tableName];
    const values = Object.values(fields);
    return (
      values.some((value) => value === true) &&
      !values.every((value) => value === true)
    );
  };

  const onExportClick = () => {
    dispatch(
      getPartnersReportsSagaAction({
        partner_status: partnerFilterProp.status.join(","),
        partner_name: partnerFilterProp.partnerName.join(","),
        partner_type: partnerFilterProp.type.join(","),
        partner_tier: partnerFilterProp.tier.join(","),
        partner_vertical: partnerFilterProp.vertical.join(","),
        revenue_stream: partnerFilterProp.revenueStream.join(","),
        account_manager: partnerFilterProp.accountManager.join(","),
        region: partnerFilterProp.region.join(","),
        country: partnerFilterProp.country.join(","),
        contract_name: partnerFilterProp.contractName.join(","),
        contract_status: partnerFilterProp.contractStatus.join(","),
        support_level: partnerFilterProp.supportLevel.join(","),
        start_date_range: partnerFilterProp.startDateRange,
        end_date_range: partnerFilterProp.endDateRange,
        saved_export_format: selectedFormat?.label || "hid-report",
        selected_options: exportFields,
      })
    );
  };

  const toTitleCase = (string: string): string => {
    return string
      .toLowerCase()
      .split(" ")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  const getDefaultExportReportFormat = (): ExportReportFormat => ({
    partnerFields: {
      partner_name: false,
      country: false,
      account_manager: false,
      region: false,
      address: false,
      partner_type: false,
      partner_tier: false,
      partner_vertical: false,
      website: false,
      description: false,
      revenue_stream: false,
      phone_number: false,
      partner_potential: false,
      support_level: false,
      registration_date: false,
      partner_status: false,
    },
    contractFields: {
      contract_status: false,
      new_contract: false,
      work_package_type: false,
      subscription_enabled: false,
      start_date: false,
      end_date: false,
    },
    revenueFields: {
      work_package_amount: false,
    },
  });

  const transformExportFormat = (
    format: ExportReportFormat | null
  ): ExportReportFormat => {
    const defaultFormat = getDefaultExportReportFormat();

    if (!format) return defaultFormat;

    return {
      partnerFields: {
        ...defaultFormat.partnerFields,
        ...format.partnerFields,
      },
      contractFields: {
        ...defaultFormat.contractFields,
        ...format.contractFields,
      },
      revenueFields: {
        ...defaultFormat.revenueFields,
        ...format.revenueFields,
      },
    };
  };

  const onReportFormatSelect = (
    data: { label: string; value: string } | null
  ) => {
    setSelectedFormat(data);
    if (data) {
      const selected = reportFormatsList.find(
        (format) => format.ID === Number(data.value)
      );
      if (selected) {
        const updatedOptions = transformExportFormat(selected.selected_options);
        setExportFields(updatedOptions);
      }
    } else {
      const resetOptions = transformExportFormat(null);
      setExportFields(resetOptions);
    }
  };

  const onDeleteButtonClick = () => {
    if (selectedFormat) {
      dispatch(
        deleteReportFormatSagaAction({
          id: Number(selectedFormat.value),
          setModalOpen: setDeleteModalOpen,
        })
      );
    }
  };

  const renderFieldSection = (
    sectionName: FieldSection,
    icon: string,
    label: string
  ) => (
    <div className="gray-container" key={sectionName}>
      <div className="header-container">
        <div className="icon-label">
          <Iconography icon={icon} size={IconographySizes.Medium} />
          <label>{label}</label>
        </div>
        <Checkbox
          name="Select All"
          value={`select-all-${sectionName}`}
          checked={isAllSelected(sectionName)}
          indeterminate={isIndeterminate(sectionName)}
          id={`select-all-${sectionName}`}
          onChange={(e, eventResult) =>
            onSelectAllClick(sectionName, eventResult.checked)
          }
        />
      </div>
      <div className="values-wrapper">
        {Object.entries(exportFields[sectionName]).map((field) => {
          const [key, value] = field;
          const fieldName = toTitleCase(key.replaceAll("_", " "));
          return (
            <div className="checkbox-wrapper" key={key}>
              <Checkbox
                name={fieldName}
                value={key}
                checked={value}
                id={key}
                onChange={(e, eventResult) =>
                  onCheckboxClick(eventResult, sectionName)
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        headerText="Export Partners"
        width={512}
        onClose={() => setModalOpen(false)}
        className="export-report-modal"
      >
        <div className="fields-container">
          <div className="heading">
            Choose what to include in exported partner report
          </div>
          <Dropdown
            id="savedFormats"
            label="Select Saved Export Formats"
            placeholder="Choose Saved Export Format"
            theme={dropDownThemes.LIGHT}
            options={reportFormatOptions}
            isClearable={true}
            isSearchable={true}
            selected={selectedFormat}
            onSelect={onReportFormatSelect}
          />
          {renderFieldSection("partnerFields", "building", "Partner Details")}
          {renderFieldSection("contractFields", "memo", "Contract Details")}
          {renderFieldSection("revenueFields", "memo", "Revenue Details")}
          <div className="button-group">
            <div className="left">
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => setModalOpen(false)}
              />
            </div>
            <div className="right">
              <Button
                variant="secondary"
                label="Save"
                onClick={() => setSaveModalOpen(true)}
              />
              <Button
                variant="warning"
                label="Delete"
                onClick={() => setDeleteModalOpen(true)}
                disabled={!selectedFormat}
              />
              <Button
                variant="primary"
                label="Export"
                disabled={status.partnersReportLoading}
                onClick={onExportClick}
              />
            </div>
          </div>
        </div>
      </Modal>
      {saveModalOpen && (
        <SaveReportFormatModal
          reportFormat={exportFields}
          setModalOpen={setSaveModalOpen}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          setDeleteOpen={setDeleteModalOpen}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteButtonLoading={status.deleteReportFormatLoading}
          message="Are you sure you want to delete this report format?"
        />
      )}
    </>
  );
};

const SaveReportFormatModal: React.FC<{
  reportFormat: ExportReportFormat;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ reportFormat, setModalOpen }) => {
  const [formatName, setFormatName] = React.useState<string>("");
  const [warningMessage, setWarningMessage] = React.useState<string>("");
  const { reportFormatsList, status } = useAppSelector(
    (state) => state.reports
  );
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const selected = reportFormatsList.find(
      (format) => format.saved_export_format === formatName
    );
    if (selected) {
      setWarningMessage(
        `Saving will override report format: ${selected.saved_export_format}`
      );
    } else {
      setWarningMessage("");
    }
  }, [formatName, reportFormatsList]);

  const onSaveButtonClick = () => {
    dispatch(
      saveReportFormatSagaAction({
        payload: {
          saved_export_format: formatName,
          selected_options: reportFormat,
        },
        setModalOpen: setModalOpen,
      })
    );
  };

  return (
    <Modal
      headerText="Save Export Format"
      width={512}
      onClose={() => setModalOpen(false)}
      className="save-report-format-modal"
    >
      <div className="message-button-container">
        <div className="label-input-wrapper">
          <label>
            Name<span>*</span>
          </label>
          <TextField
            id="formatName"
            name="formatName"
            placeholder=""
            value={formatName}
            theme="dark"
            helperText={warningMessage}
            onChange={(e) => setFormatName(e.currentTarget.value)}
          />
        </div>
        <div className="button-group">
          <Button
            variant="secondary"
            label="Back"
            onClick={() => setModalOpen(false)}
          />
          <div className="save-button-wrapper">
            <Button
              variant="primary"
              label="Save"
              disabled={status.saveReportFormatLoading || !formatName}
              onClick={onSaveButtonClick}
            />
            {status.saveReportFormatLoading && <Loader />}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportReportModal;
